import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography
} from '@mui/material';
import MaterialTable from 'material-table'
import { makeStyles } from '@mui/styles';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';
import LastPageIcon from '@mui/icons-material/LastPage';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import * as XLSX from 'xlsx'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddBoxIcon from '@mui/icons-material/AddBox';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RemoveIcon from '@mui/icons-material/Remove';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import TableChartIcon from '@mui/icons-material/TableChart';
import HistoryIcon from '@mui/icons-material/History';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import React, { forwardRef, useMemo } from 'react';
import axios from 'src/utils/axios';
import { DataGrid, trTR } from '@mui/x-data-grid';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { getAbsPreviousUploads } from 'src/store/slices/dynamicCatalogSlice';
import { getUser } from 'src/utils/Common';
import AlertAndLoading from 'src/components/AlertAndLoading';
import {
  DARK,
  ORANGE,
  darkButtonSx,
  dialogPaperProps,
  lightButtonSx,
  useDynamicStyles
} from 'src/components/dynamicCatalogStyles';
import DynamicListToolbar, { filterDynamicRows } from 'src/components/DynamicListToolbar';

const useStyles = makeStyles(() => ({
  fileBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    height: '36px',
    padding: '0 12px',
    borderRadius: '10px',
    border: `1px dashed ${ORANGE}`,
    backgroundColor: '#fffaf2',
    cursor: 'pointer',
    boxSizing: 'border-box',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#fff3e0'
    }
  },
  infoBox: {
    padding: '8px 12px',
    backgroundColor: '#fafafa',
    borderRadius: '10px',
    border: '1px solid #e0e0e0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    position: 'relative',
    overflow: 'hidden',
    textAlign: 'center',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '3px',
      backgroundColor: ORANGE
    }
  },
  infoLabel: {
    fontWeight: '600',
    fontSize: '10px',
    color: '#546e7a',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  infoValue: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#263238'
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 16px'
  },
  tableWrapper: {
    '& .MuiTableCell-root': {
      fontSize: '12px'
    },
    '& .MuiTableRow-root:hover': {
      backgroundColor: '#fff8e1'
    },
    '& .MuiToolbar-root': {
      minHeight: '48px'
    }
  }
}));

const EXTENSIONS = ['xlsx', 'xls', 'csv']
const columns = ["KulModelKodu", "Fiyat"]

const SAMPLE_ROWS = [{ KulModelKodu: '7296303210', Fiyat: '9576' },
{ KulModelKodu: '7296123210', Fiyat: '9875' },
{ KulModelKodu: '7296123291  ', Fiyat: '10408' },
{ KulModelKodu: '7295923210  ', Fiyat: '10406' },
{ KulModelKodu: '7295923281  ', Fiyat: '10403' },
{ KulModelKodu: '7237023212  ', Fiyat: '3462' },
{ KulModelKodu: '7237023281  ', Fiyat: '3469' },
{ KulModelKodu: '7262423214  ', Fiyat: '3123' },
{ KulModelKodu: '7283023214  ', Fiyat: '3461' },
{ KulModelKodu: '7704330201  ', Fiyat: '3465' },
]

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBoxIcon {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <CheckIcon {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <ClearIcon {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutlineIcon {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => <ChevronRightIcon {...props} ref={ref} />),
  Edit: forwardRef((props, ref) => <EditIcon {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAltIcon {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterListIcon {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPageIcon {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPageIcon {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRightIcon {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => <ChevronLeftIcon {...props} ref={ref} />),
  ResetSearch: forwardRef((props, ref) => <ClearIcon {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <SearchIcon {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownwardIcon {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <RemoveIcon {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumnIcon {...props} ref={ref} />)
};

const hasCell = (v) => v !== undefined && v !== null && String(v).trim() !== '';
const isInvalidRow = (r) => !String(r.KulModelKodu ?? '').trim() || r.Fiyat === undefined || r.Fiyat === '' || isNaN(Number(r.Fiyat));

function AbsPriceUploadDynamic() {
  const classes = useStyles();
  const dc = useDynamicStyles();
  const dispatch = useDispatch();
  const SessionUser = getUser();

  const [colDefs, setColDefs] = React.useState()
  const [saveFileData, setSaveFileData] = React.useState([]);
  const [fileName, setFileName] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [responseMessage, setresponseMessage] = React.useState('');
  const [alertType, setAlertType] = React.useState({ ErrorType: 'error', ErrorTitle: 'Uyarı' });
  const [uyari, setUyari] = React.useState(false);
  const [previousUploadsPopUp, setPreviousUploadsPopup] = React.useState(false);
  const [previousUploadsData, setPreviousUploadsData] = React.useState([]);
  const [previousUploadsLoading, setPreviousUploadsLoading] = React.useState(false);
  const [previousUploadsLog, setPreviousUploadsLog] = React.useState([]);
  const [historySearch, setHistorySearch] = React.useState('');

  function InfoAlert(ErrorDescription, ErrorType, ErrorTitle) {
    setresponseMessage(ErrorDescription);
    setAlertType({ ErrorType: ErrorType, ErrorTitle: ErrorTitle });
    setUyari(true);
  }

  const uyariKapat = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setUyari(false);
  };

  const getExention = (file) => {
    const parts = file.name.split('.')
    const extension = parts[parts.length - 1]
    return EXTENSIONS.includes(extension) // return boolean
  }

  const importExcel = (e) => {
    const files = e.target.files;
    if (files.length) {
      const file = files[0];
      if (!getExention(file)) {
        InfoAlert('Lütfen .xlsx, .xls veya .csv uzantılı bir dosya seçiniz.', 'error', 'Hata');
        return;
      }
      setFileName(file.name);
      const reader = new FileReader();
      setLoading(true)
      reader.readAsBinaryString(file)
      reader.onload = (event) => {
        //parse data
        const bstr = event.target.result
        const workBook = XLSX.read(bstr, { type: "binary" })
        //get first sheet
        const workSheetName = workBook.SheetNames[0]
        const workSheet = workBook.Sheets[workSheetName]
        //convert to array; tamamen boş satırlar atlanır
        const fileData = XLSX.utils.sheet_to_json(workSheet, { header: 1 })
          .filter((row, idx) => idx === 0 || (row && (hasCell(row[0]) || hasCell(row[1]))))
        const tempResult = []
        for (var i = 1; i < fileData.length; i++) {
          tempResult.push({
            KulModelKodu: String(fileData[i][0] ?? ''),
            Fiyat: fileData[i][1]
          })
        }

        /* GEÇMİŞ İŞLEMLERİ LOGLAMA */
        const LogtempResult = []
        for (var j = 1; j < fileData.length; j++) {
          LogtempResult.push({
            KulModelKodu: String(fileData[j][0] ?? ''),
            Fiyat: fileData[j][1],
            SessionUser: SessionUser?.FirstName + " " + SessionUser?.LastName
          })
        }

        setSaveFileData(tempResult);
        setPreviousUploadsLog(LogtempResult);
        setColDefs(columns.map(head => ({ title: head, field: head })))
        setLoading(false)
      }
      reader.onerror = () => {
        setLoading(false);
        InfoAlert('Dosya okunamadı.', 'error', 'Hata');
      }
    }
  }

  const clearFile = () => {
    setSaveFileData([]);
    setPreviousUploadsLog([]);
    setColDefs(undefined);
    setFileName('');
  };

  const dfSave = () => {
    const data = saveFileData.map(i => ({
      UserModelCode: i.KulModelKodu,
      Price: i.Fiyat
    }));

    /* GEÇMİŞ İŞLEMLERİ LOGLAMA */
    const dataLog = previousUploadsLog.map(i => ({
      UserModelCode: i.KulModelKodu,
      Price: i.Fiyat,
      SessionUser: SessionUser.FirstName + " " + SessionUser.LastName
    }));

    setSaving(true);
    Promise.all([
      axios.post('SetAbsPriceUpload', data, { headers: { 'Content-Type': 'application/json' } }),
      axios.post('SetAbsPreviousUploadsLog', dataLog, { headers: { 'Content-Type': 'application/json' } })
    ]).then(() => {
      setSaving(false);
      clearFile();
      setPreviousUploadsData([]);
      InfoAlert('İşleminiz başarılıyla gerçekleşmiştir...', 'success', 'Başarılı');
    }).catch(() => {
      setSaving(false);
      InfoAlert('Kaydedilemedi... Lütfen daha sonra tekrar deneyiniz...', 'error', 'Hata');
    });
  }

  const downloadSample = () => {
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(SAMPLE_ROWS), 'Report');
    XLSX.writeFile(workbook, 'Örnek Excel.xlsx');
  };

  function loadPreviousUploads() {
    setPreviousUploadsLoading(true);
    dispatch(getAbsPreviousUploads()).then(response => {
      if (response.meta?.requestStatus === 'fulfilled') {
        setPreviousUploadsData(Array.isArray(response.payload) ? response.payload : []);
      }
      setPreviousUploadsLoading(false);
    })
  }

  function PreviousUploadsClicked() {
    setHistorySearch('');
    setPreviousUploadsPopup(true);
    loadPreviousUploads();
  }

  const previousUploadsColumns = [
    { field: 'KUL_MODEL_KODU', headerName: 'Ürün SKU', flex: 1, minWidth: 140 },
    { field: 'MODEL_KODU_ACIKLAMA', headerName: 'Model', flex: 1.6, minWidth: 200 },
    {
      field: 'FIYAT', headerName: 'Fiyat', width: 130, align: 'right', headerAlign: 'right',
      valueFormatter: (p) => (p.value === null || p.value === undefined || isNaN(Number(p.value)) ? p.value : Number(p.value).toLocaleString('tr-TR'))
    },
    { field: 'YUKLEYEN_KISI', headerName: 'Yükleyen Kişi', flex: 1, minWidth: 160 },
    {
      field: 'YUKLENEN_TARIH', headerName: 'Yüklenme Tarihi', width: 150,
      valueFormatter: (p) => (p.value ? moment(p.value).format('DD.MM.YYYY HH:mm') : '')
    }
  ]

  const filteredHistory = useMemo(() => filterDynamicRows(previousUploadsData, {
    searchText: historySearch,
    searchFields: ['KUL_MODEL_KODU', 'MODEL_KODU_ACIKLAMA', 'YUKLEYEN_KISI']
  }), [previousUploadsData, historySearch]);

  const invalidCount = saveFileData.filter(isInvalidRow).length;
  const summaryItems = [
    { label: 'Dosya', value: fileName || '-' },
    { label: 'Yüklenen Satır', value: saveFileData.length },
    { label: 'Hatalı Görünen Satır', value: invalidCount, warn: invalidCount > 0 }
  ];

  return (
    <>
      <AlertAndLoading Message={responseMessage} open={uyari} AlertType={alertType} onClose={uyariKapat} loading={loading} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Yükleme */}
        <Card className={dc.card}>
          <CardHeader
            className={dc.cardHeader}
            avatar={<PriceChangeIcon sx={{ color: ORANGE }} />}
            title="Abs Fiyat Yükleme"
            subheader="Örnek excel formatında yükleyiniz; ilk kolon kullanıcı model kodu, ikinci kolon fiyat olmalıdır"
            action={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" sx={lightButtonSx} startIcon={<CloudDownloadOutlinedIcon />} onClick={downloadSample}>
                  Örnek Excel
                </Button>
                <Button size="small" sx={lightButtonSx} startIcon={<HistoryIcon />} onClick={PreviousUploadsClicked}>
                  Geçmiş Yüklemeler
                </Button>
              </Box>
            }
          />
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={8} md={6} lg={5}>
                <input
                  id="AbsFileInput"
                  type='file'
                  onChange={importExcel}
                  onClick={(event) => { event.target.value = null }}
                  hidden
                  accept=".xlsx, .xls, .csv"
                >
                </input>
                <label htmlFor="AbsFileInput">
                  <Box className={classes.fileBox}>
                    <UploadFileIcon sx={{ color: ORANGE, fontSize: 20 }} />
                    <Typography noWrap sx={{ fontSize: '13px', color: fileName ? '#263238' : '#90a4ae', fontWeight: fileName ? 600 : 400, flex: 1 }}>
                      {fileName || 'Excel dosyası seçin (.xlsx, .xls)'}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: ORANGE, whiteSpace: 'nowrap' }}>
                      {fileName ? 'Değiştir' : 'Dosya Seç'}
                    </Typography>
                    {fileName && (
                      <Tooltip title="Temizle">
                        <IconButton
                          size="small"
                          sx={{ p: 0.25, color: '#90a4ae', '&:hover': { color: '#c62828' } }}
                          onClick={(e) => {
                            // label içinde olduğu için dosya seçme penceresini açmasın
                            e.preventDefault();
                            e.stopPropagation();
                            clearFile();
                          }}
                        >
                          <ClearIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </label>
              </Grid>
              <Grid item xs={12} sm={4} md={2} lg={1.5} sx={{ ml: { sm: 'auto' } }}>
                <Button
                  fullWidth
                  startIcon={saving ? <CircularProgress size={14} sx={{ color: ORANGE }} /> : <SaveIcon />}
                  sx={darkButtonSx}
                  disabled={saving || saveFileData.length === 0}
                  onClick={dfSave}
                >
                  Kaydet
                </Button>
              </Grid>
            </Grid>

            <Grid container spacing={1.5} sx={{ mt: 1 }}>
              {summaryItems.map((item) => (
                <Grid item xs={12} sm={4} key={item.label}>
                  <Box className={classes.infoBox}>
                    <Typography className={classes.infoLabel}>{item.label}</Typography>
                    <Typography className={classes.infoValue} noWrap sx={item.warn ? { color: '#e65100 !important' } : undefined}>{item.value}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* Önizleme */}
        <Card className={dc.card}>
          <CardHeader
            className={dc.cardHeader}
            avatar={<TableChartIcon sx={{ color: ORANGE }} />}
            title="Fiyat Önizleme"
            subheader={saveFileData.length > 0 ? 'Kaydetmeden önce yüklenen satırları kontrol edin' : undefined}
          />
          {saveFileData.length === 0 ? (
            <Box className={classes.emptyState}>
              <UploadFileIcon sx={{ fontSize: 44, color: '#cfd8dc' }} />
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#78909c', mt: 1 }}>
                Henüz dosya yüklenmedi
              </Typography>
              <Typography sx={{ fontSize: '12px', color: '#90a4ae' }}>
                Yüklediğiniz dosyadaki satırlar burada listelenecek.
              </Typography>
            </Box>
          ) : (
            <Box className={classes.tableWrapper}>
              <MaterialTable
                title=""
                data={saveFileData}
                columns={colDefs}
                icons={tableIcons}
                components={{ Container: (props) => <Paper {...props} elevation={0} /> }}
                options={{
                  pageSize: 10,
                  pageSizeOptions: [10, 25, 50, 100],
                  headerStyle: { backgroundColor: '#f5f5f5', color: DARK, fontSize: '11px', fontWeight: 700, padding: '10px 16px' },
                  rowStyle: (row) => ({ fontSize: '12px', backgroundColor: isInvalidRow(row) ? '#fff3e0' : undefined }),
                  searchFieldStyle: { fontSize: '13px' }
                }}
                localization={{
                  toolbar: { searchPlaceholder: 'Ara' },
                  pagination: { labelRowsSelect: 'satır', labelDisplayedRows: '{from}-{to} / {count}' },
                  body: { emptyDataSourceMessage: 'Kayıt bulunamadı' }
                }}
              />
            </Box>
          )}
        </Card>
      </Box>

      {/* Geçmiş yüklemeler */}
      <Dialog
        open={previousUploadsPopUp}
        onClose={() => setPreviousUploadsPopup(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={dialogPaperProps}
      >
        <DialogTitle className={dc.dialogTitle}>
          Geçmiş Yüklemeler
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Yenile">
              <span>
                <IconButton size="small" onClick={loadPreviousUploads} disabled={previousUploadsLoading}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton size="small" onClick={() => setPreviousUploadsPopup(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <DynamicListToolbar
            searchText={historySearch}
            onSearchChange={setHistorySearch}
            placeholder="SKU, model veya kişi ara"
            shownCount={filteredHistory.length}
          >
            <Box sx={{ ml: 'auto', fontSize: '11px', color: '#78909c' }}>
              {previousUploadsLoading ? 'Yükleniyor…' : `${previousUploadsData.length} kayıt`}
            </Box>
          </DynamicListToolbar>
          <DataGrid
            className={dc.grid}
            style={{ height: 520, width: '100%' }}
            columns={previousUploadsColumns}
            rows={filteredHistory}
            loading={previousUploadsLoading}
            rowHeight={40}
            headerHeight={42}
            disableSelectionOnClick
            getRowId={(rows) => rows.ID}
            localeText={{ ...trTR.components.MuiDataGrid.defaultProps.localeText, noRowsLabel: 'Kayıt bulunamadı.' }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AbsPriceUploadDynamic;
